import { supabase } from "@/integrations/supabase/client";

export const fetchMyShifts = async (profileId: string, weekStartISO: string, weekEndISO: string) => {
  // Ensure user context is set before making queries that involve RLS policies
  await supabase.rpc('ensure_user_context');
  
  // Step 1: Fetch shifts without the problematic relationship
  const { data: shiftsData, error: shiftsError } = await supabase
    .from('shifts')
    .select(`
      id, 
      profile_id, 
      start_time, 
      end_time, 
      note,
      shift_code
    `)
    .eq('profile_id', profileId)
    .lt('start_time', weekEndISO)
    .gt('end_time', weekStartISO)
    .order('start_time');
  
  if (shiftsError) throw shiftsError;
  
  if (!shiftsData || shiftsData.length === 0) {
    return [];
  }
  
  // Step 2: Fetch shift_swaps using the new RPC function to avoid RLS issues
  const shiftIds = shiftsData.map(shift => shift.id);
  const { data: swapsData, error: swapsError } = await supabase
    .rpc('get_my_shift_swaps', { p_shift_ids: shiftIds });
  
  if (swapsError) {
    console.warn('Failed to fetch shift_swaps:', swapsError);
    // Continue without swap data rather than failing completely
  }
  
  
  // Step 3: Create a map of shift_id -> active swap status
  const swapStatusMap = new Map();
  if (swapsData) {
    for (const swap of swapsData) {
      // Only consider active swap statuses
      if (['open', 'accepted', 'manager_pending'].includes(swap.status)) {
        swapStatusMap.set(swap.requester_shift_id, swap.status);
      }
    }
  }
  
  
  // Step 4: Fetch shift code details
  const shiftCodes = [...new Set(shiftsData
    .map(shift => shift.shift_code)
    .filter(Boolean)
  )];
  
  let shiftCodesMap = new Map();
  
  if (shiftCodes.length > 0) {
    const { data: codes, error: codesError } = await supabase
      .from('shift_codes')
      .select('code, name, color, category')
      .in('code', shiftCodes);
    
    if (codesError) {
      console.warn('Failed to fetch shift codes:', codesError);
    } else {
      shiftCodesMap = new Map(
        (codes ?? []).map(code => [code.code, code])
      );
    }
  }
  
  // Step 5: Transform the data to include swap status and shift_code_info
  const shifts = shiftsData.map(shift => ({
    ...shift,
    swap_status: swapStatusMap.get(shift.id) || null,
    shift_code_info: shift.shift_code ? shiftCodesMap.get(shift.shift_code) || null : null
  }));
  
  
  return shifts;
};

export const fetchCompanyShifts = async (companyId: string, weekStartISO: string, weekEndISO: string) => {
  // Ensure user context is set before making queries that involve RLS policies
  await supabase.rpc('ensure_user_context');
  
  const { data, error } = await supabase
    .from('shifts')
    .select('id, profile_id, start_time, end_time, note, status, company_id, offered_by_user_id, shift_code, is_all_day')
    .eq('company_id', companyId)
    .lt('start_time', weekEndISO)
    .gt('end_time', weekStartISO)
    .order('start_time');
  
  if (error) throw error;
  
  const shifts = data ?? [];
  
  // Get unique offered_by_user_ids to fetch profile names
  const offeredByUserIds = [...new Set(shifts
    .map(shift => shift.offered_by_user_id)
    .filter(Boolean)
  )];
  
  let profilesMap = new Map();
  
  if (offeredByUserIds.length > 0) {
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('user_id, first_name, last_name')
      .in('user_id', offeredByUserIds);
    
    if (profilesError) {
      console.warn('Failed to fetch offered_by profiles:', profilesError);
    } else {
      profilesMap = new Map(
        (profiles ?? []).map(profile => [
          profile.user_id,
          `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || null
        ])
      );
    }
  }
  
  // Get unique shift codes to fetch their details
  const shiftCodes = [...new Set(shifts
    .map(shift => shift.shift_code)
    .filter(Boolean)
  )];
  
  let shiftCodesMap = new Map();
  
  if (shiftCodes.length > 0) {
    const { data: codes, error: codesError } = await supabase
      .from('shift_codes')
      .select('code, name, color, category')
      .in('code', shiftCodes);
    
    if (codesError) {
      console.warn('Failed to fetch shift codes:', codesError);
    } else {
      shiftCodesMap = new Map(
        (codes ?? []).map(code => [code.code, code])
      );
    }
  }
  
  // Add offered_by_name and shift_code_info to each shift
  return shifts.map(shift => ({
    ...shift,
    offered_by_name: shift.offered_by_user_id ? profilesMap.get(shift.offered_by_user_id) || null : null,
    shift_code_info: shift.shift_code ? shiftCodesMap.get(shift.shift_code) || null : null
  }));
};

export const fetchCompanyProfiles = async () => {
  const { data, error } = await supabase.rpc('list_company_profiles');
  if (error) throw error;
  return data ?? [];
};

export const fetchOpenShifts = async (weekStartISO: string, weekEndISO: string) => {
  // Ensure user context is set before making queries that involve RLS policies
  await supabase.rpc('ensure_user_context');
  
  const { data, error } = await supabase.rpc('list_open_shifts', {
    p_start_time: weekStartISO,
    p_end_time: weekEndISO
  });

  if (error) throw error;
  return data ?? [];
};
